import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowPathIcon,
  CloudArrowUpIcon,
  PlayCircleIcon,
  TableCellsIcon,
} from '@heroicons/react/24/outline';
import {
  MatterMigrationJob,
  MatterMigrationManifestRequest,
  MatterMigrationOptions,
  MatterMigrationRow,
  useMatterMigration,
} from '../../../hooks/useMatterMigration';

const EXAMPLE_ROWS = [
  {
    externalClientId: 'legacy-client-1001',
    externalMatterId: 'legacy-matter-551',
    clientName: 'Acme Contracting LLC',
    clientEmail: 'ops@acme-contracting.com',
    matterNumber: 'ACME-2024-0193',
    matterDescription: 'Legacy commercial dispute migration',
    practiceArea: 'Commercial Litigation',
    priority: 'high',
    stage: 'active',
    documents: [
      {
        externalDocumentId: 'legacy-doc-9001',
        title: 'Legacy Complaint',
        contentText: 'Imported from legacy system for migration validation.',
      },
    ],
  },
];

const DEFAULT_OPTIONS: MatterMigrationOptions = {
  preserveMatterNumbers: true,
  onExistingMatter: 'reuse',
  onExistingDocument: 'rename',
  continueOnError: true,
};

const statusClassName = (status: string): string => {
  switch (status) {
    case 'completed':
    case 'imported':
      return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300';
    case 'completed_with_errors':
    case 'failed':
      return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300';
    case 'running':
    case 'queued':
    case 'validated':
      return 'bg-lex-accent-soft text-lex-accent dark:bg-lex-accent-soft dark:text-lex-accent';
    case 'skipped':
      return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
    default:
      return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
  }
};

export const MigrationCenterPanel: React.FC = () => {
  const {
    createDryRun,
    createImportJob,
    executeImportJob,
    listJobs,
    getJob,
    getJobRows,
  } = useMatterMigration();

  const [sourceSystem, setSourceSystem] = useState('legacy-dms');
  const [rowsJson, setRowsJson] = useState(JSON.stringify(EXAMPLE_ROWS, null, 2));
  const [options, setOptions] = useState<MatterMigrationOptions>(DEFAULT_OPTIONS);
  const [jobs, setJobs] = useState<MatterMigrationJob[]>([]);
  const [selectedJobId, setSelectedJobId] = useState<string>('');
  const [selectedJobRows, setSelectedJobRows] = useState<MatterMigrationRow[]>([]);
  const [selectedRowsTotal, setSelectedRowsTotal] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingJobs, setIsLoadingJobs] = useState(false);
  const [isLoadingRows, setIsLoadingRows] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const selectedJob = useMemo(
    () => jobs.find((job) => job.migration_job_id === selectedJobId) || null,
    [jobs, selectedJobId]
  );

  const parseManifest = useCallback((): MatterMigrationManifestRequest => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(rowsJson);
    } catch {
      throw new Error('Rows JSON is invalid.');
    }

    const rows = Array.isArray(parsed)
      ? parsed
      : parsed && typeof parsed === 'object' && Array.isArray((parsed as any).rows)
        ? (parsed as any).rows
        : null;

    if (!rows || rows.length === 0) {
      throw new Error('Rows JSON must contain at least one row.');
    }

    return {
      sourceSystem: sourceSystem.trim(),
      rows,
      options,
    };
  }, [options, rowsJson, sourceSystem]);

  const refreshJobs = useCallback(async () => {
    setIsLoadingJobs(true);
    try {
      const response = await listJobs(50);
      setJobs(response.jobs || []);
      if (!selectedJobId && response.jobs?.length) {
        setSelectedJobId(response.jobs[0].migration_job_id);
      }
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to load migration jobs.');
    } finally {
      setIsLoadingJobs(false);
    }
  }, [listJobs, selectedJobId]);

  const refreshSelectedJob = useCallback(async () => {
    if (!selectedJobId) {
      return;
    }

    try {
      const response = await getJob(selectedJobId);
      setJobs((prev) => {
        const existingIndex = prev.findIndex((job) => job.migration_job_id === selectedJobId);
        if (existingIndex === -1) {
          return [response.job, ...prev];
        }
        const next = [...prev];
        next[existingIndex] = response.job;
        return next;
      });
    } catch (error) {
      console.error(error);
    }
  }, [getJob, selectedJobId]);

  const loadRows = useCallback(
    async (jobId: string) => {
      setIsLoadingRows(true);
      try {
        const response = await getJobRows(jobId, { limit: 200, offset: 0 });
        setSelectedJobRows(response.rows || []);
        setSelectedRowsTotal(response.total || 0);
      } catch (error) {
        setErrorMessage(error instanceof Error ? error.message : 'Failed to load migration rows.');
      } finally {
        setIsLoadingRows(false);
      }
    },
    [getJobRows]
  );

  useEffect(() => {
    refreshJobs();
  }, [refreshJobs]);

  useEffect(() => {
    if (selectedJobId) {
      loadRows(selectedJobId);
      refreshSelectedJob();
    } else {
      setSelectedJobRows([]);
      setSelectedRowsTotal(0);
    }
  }, [selectedJobId, loadRows, refreshSelectedJob]);

  const handleDryRun = async () => {
    setErrorMessage(null);
    setSuccessMessage(null);
    setIsSubmitting(true);
    try {
      const request = parseManifest();
      const response = await createDryRun(request);
      setSuccessMessage(
        `Dry run completed for ${response.summary.totalRows} row(s). Failed rows: ${response.summary.failedRows}.`
      );
      await refreshJobs();
      setSelectedJobId(response.job.migration_job_id);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Dry run failed.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleQueueImport = async () => {
    setErrorMessage(null);
    setSuccessMessage(null);
    setIsSubmitting(true);
    try {
      const request = parseManifest();
      const response = await createImportJob(request, true);
      setSuccessMessage(
        `Import job queued (${response.job.migration_job_id})${response.queueJobId ? ` via queue job ${response.queueJobId}` : ''}.`
      );
      await refreshJobs();
      setSelectedJobId(response.job.migration_job_id);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Queue import failed.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRunSelectedNow = async () => {
    if (!selectedJobId) {
      return;
    }

    setErrorMessage(null);
    setSuccessMessage(null);
    setIsSubmitting(true);
    try {
      const response = await executeImportJob(selectedJobId);
      setSuccessMessage(`Job ${response.job.migration_job_id} executed with status ${response.job.status}.`);
      await refreshJobs();
      await loadRows(selectedJobId);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to execute selected job.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Migration Center</h2>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Bulk-import clients, matters, and documents from legacy systems while preserving matter numbers.
        </p>
      </div>

      {errorMessage && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
          {errorMessage}
        </div>
      )}

      {successMessage && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300">
          {successMessage}
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-900">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">Manifest Builder</h3>
            <TableCellsIcon className="h-5 w-5 text-gray-400" />
          </div>

          <div className="mt-4 space-y-4">
            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Source System
              </label>
              <input
                type="text"
                value={sourceSystem}
                onChange={(event) => setSourceSystem(event.target.value)}
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-lex-accent focus:ring-2 focus:ring-lex-accent/30 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                placeholder="legacy-dms"
              />
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={options.preserveMatterNumbers}
                  onChange={(event) =>
                    setOptions((prev) => ({
                      ...prev,
                      preserveMatterNumbers: event.target.checked,
                    }))
                  }
                />
                Preserve Matter Numbers
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={options.continueOnError}
                  onChange={(event) =>
                    setOptions((prev) => ({
                      ...prev,
                      continueOnError: event.target.checked,
                    }))
                  }
                />
                Continue On Error
              </label>
              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Existing Matter Policy
                </label>
                <select
                  value={options.onExistingMatter}
                  onChange={(event) =>
                    setOptions((prev) => ({
                      ...prev,
                      onExistingMatter: event.target.value as MatterMigrationOptions['onExistingMatter'],
                    }))
                  }
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                >
                  <option value="reuse">Reuse</option>
                  <option value="skip">Skip</option>
                  <option value="error">Error</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Existing Document Policy
                </label>
                <select
                  value={options.onExistingDocument}
                  onChange={(event) =>
                    setOptions((prev) => ({
                      ...prev,
                      onExistingDocument: event.target.value as MatterMigrationOptions['onExistingDocument'],
                    }))
                  }
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                >
                  <option value="rename">Rename</option>
                  <option value="skip">Skip</option>
                  <option value="error">Error</option>
                </select>
              </div>
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Rows JSON
              </label>
              <textarea
                value={rowsJson}
                onChange={(event) => setRowsJson(event.target.value)}
                rows={14}
                className="w-full rounded-md border border-gray-300 bg-gray-50 px-3 py-2 font-mono text-xs text-gray-900 outline-none focus:border-lex-accent focus:ring-2 focus:ring-lex-accent/30 dark:border-gray-600 dark:bg-gray-950 dark:text-gray-100"
              />
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                onClick={handleDryRun}
                disabled={isSubmitting}
                className="inline-flex items-center gap-2 rounded-md border border-lex-accent bg-lex-accent-soft px-3 py-2 text-sm font-medium text-lex-accent hover:bg-lex-state-hover disabled:opacity-60 dark:border-lex-accent dark:bg-lex-accent-soft dark:text-lex-accent"
              >
                <PlayCircleIcon className="h-4 w-4" />
                Run Dry-Run
              </button>
              <button
                onClick={handleQueueImport}
                disabled={isSubmitting}
                className="inline-flex items-center gap-2 rounded-md bg-lex-accent px-3 py-2 text-sm font-medium text-lex-accent-contrast hover:bg-lex-accent-hover disabled:opacity-60"
              >
                <CloudArrowUpIcon className="h-4 w-4" />
                Queue Import
              </button>
            </div>
          </div>
        </section>

        <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-900">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">Migration Jobs</h3>
            <button
              onClick={refreshJobs}
              className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2.5 py-1.5 text-xs text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
              disabled={isLoadingJobs}
            >
              <ArrowPathIcon className="h-4 w-4" />
              Refresh
            </button>
          </div>

          <div className="mt-4 max-h-80 overflow-auto rounded-md border border-gray-200 dark:border-gray-700">
            <table className="min-w-full text-left text-xs">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-3 py-2 font-medium text-gray-600 dark:text-gray-300">Job</th>
                  <th className="px-3 py-2 font-medium text-gray-600 dark:text-gray-300">Status</th>
                  <th className="px-3 py-2 font-medium text-gray-600 dark:text-gray-300">Rows</th>
                  <th className="px-3 py-2 font-medium text-gray-600 dark:text-gray-300">Created</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => (
                  <tr
                    key={job.migration_job_id}
                    onClick={() => setSelectedJobId(job.migration_job_id)}
                    className={`cursor-pointer border-t border-gray-200 dark:border-gray-700 ${
                      selectedJobId === job.migration_job_id ? 'bg-lex-accent-soft dark:bg-lex-accent-soft' : ''
                    }`}
                  >
                    <td className="px-3 py-2 text-gray-700 dark:text-gray-200">
                      <div className="font-medium">{job.migration_job_id.slice(0, 8)}</div>
                      <div className="text-[11px] text-gray-500 dark:text-gray-400">{job.source_system}</div>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${statusClassName(job.status)}`}>
                        {job.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-gray-700 dark:text-gray-200">
                      {job.summary?.totalRows ?? 0}
                    </td>
                    <td className="px-3 py-2 text-gray-500 dark:text-gray-400">
                      {new Date(job.created_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
                {jobs.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-3 py-4 text-center text-gray-500 dark:text-gray-400">
                      No migration jobs yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {selectedJob && (
            <div className="mt-4 space-y-3 rounded-md border border-gray-200 p-3 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium text-gray-800 dark:text-gray-100">
                  Selected Job: {selectedJob.migration_job_id}
                </div>
                <button
                  onClick={handleRunSelectedNow}
                  disabled={isSubmitting}
                  className="inline-flex items-center gap-1 rounded-md border border-lex-accent bg-lex-accent-soft px-2.5 py-1.5 text-xs text-lex-accent hover:bg-lex-state-hover disabled:opacity-60 dark:border-lex-accent dark:bg-lex-accent-soft dark:text-lex-accent"
                >
                  <PlayCircleIcon className="h-4 w-4" />
                  Run Now
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 dark:text-gray-300 md:grid-cols-4">
                <div>Total: {selectedJob.summary?.totalRows ?? 0}</div>
                <div>Imported: {selectedJob.summary?.importedRows ?? 0}</div>
                <div>Skipped: {selectedJob.summary?.skippedRows ?? 0}</div>
                <div>Failed: {selectedJob.summary?.failedRows ?? 0}</div>
              </div>
              {selectedJob.error_message && (
                <div className="rounded border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
                  {selectedJob.error_message}
                </div>
              )}
            </div>
          )}
        </section>
      </div>

      <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-900">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">
            Selected Job Rows ({selectedRowsTotal})
          </h3>
          {isLoadingRows && <span className="text-xs text-gray-500 dark:text-gray-400">Loading rows...</span>}
        </div>

        <div className="mt-4 max-h-96 overflow-auto rounded-md border border-gray-200 dark:border-gray-700">
          <table className="min-w-full text-left text-xs">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-3 py-2 font-medium text-gray-600 dark:text-gray-300">Row</th>
                <th className="px-3 py-2 font-medium text-gray-600 dark:text-gray-300">Matter Number</th>
                <th className="px-3 py-2 font-medium text-gray-600 dark:text-gray-300">Status</th>
                <th className="px-3 py-2 font-medium text-gray-600 dark:text-gray-300">Message</th>
              </tr>
            </thead>
            <tbody>
              {selectedJobRows.map((row) => (
                <tr key={row.migration_row_id} className="border-t border-gray-200 dark:border-gray-700">
                  <td className="px-3 py-2 text-gray-700 dark:text-gray-200">{row.row_number}</td>
                  <td className="px-3 py-2 text-gray-700 dark:text-gray-200">{row.matter_number || '-'}</td>
                  <td className="px-3 py-2">
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${statusClassName(row.status)}`}>
                      {row.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-gray-600 dark:text-gray-300">{row.message || '-'}</td>
                </tr>
              ))}
              {selectedJobRows.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-3 py-4 text-center text-gray-500 dark:text-gray-400">
                    No rows loaded.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default MigrationCenterPanel;
